import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import HelpModal from '../HelpModal';
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import HEXASLogo from '../assets/Logos/HEXASLogo.jpg';
import { useEffect } from 'react';
import { useTextHighlight } from '../utils/highlightUtils';

const Class14GuidedPractice01 = () => {
  const navigate = useNavigate();

  const highlightRef = useRef(null);
  useTextHighlight(highlightRef);

  const [showHelp, setShowHelp] = useState(false);

  const questionRefs = useRef([]);
  const rightColRef = useRef(null);

  // Make sure your answers state exists
  const [answers, setAnswers] = useState(Array(20).fill(''));
  const [currentQuestion, setCurrentQuestion] = useState(14);
  const onNext = () => {
    setCurrentQuestion(prev => Math.min(prev + 1, 19));
  };

  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(100 * 60); // 30 minutes in seconds
  const [showSeconds, setShowSeconds] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 0) {
          clearInterval(timer);
          navigate('/');  // Redirect to home
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [navigate]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (showSeconds) {
      return `${mins} : ${secs.toString().padStart(2, '0')} remaining`;
    }
    return `${mins} minutes remaining`;
  };

  const handleStartTest = () => {
    navigate(`/guided-practice-01-form`);
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;
    const time = parseFloat(e.target.value);
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const handleVolumeChange = (e) => {
    if (!audioRef.current) return;
    const vol = parseFloat(e.target.value);
    setVolume(vol);
    audioRef.current.volume = vol;
    setIsMuted(vol === 0);
  };

  const handleMute = () => {
    if (!audioRef.current) return;
    if (isMuted) {
      audioRef.current.muted = false;
      setIsMuted(false);
      audioRef.current.volume = volume;
    } else {
      audioRef.current.muted = true;
      setIsMuted(true);
    }
  };

  const GreenCheck = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="inline-block"
    >
      <path
        d="M7 11L9 13.5L16 7"
        stroke="#7DD77D"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );


  return (
    <div className="min-h-screen bg-white flex flex-col font-[Arial]">
      {/* Help Modal */}
      <HelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
      />
      {/* Top Bar (fixed) */}
      <div className="sticky top-0 z-30 bg-white">
        <div className="relative h-[62px] bg-[#F4C3C3] border-b border-black flex items-center justify-between px-4">
          {/* Left + Center combined on left side */}
          <div className="flex flex-col justify-center space-y-0.5 pl-6">
            <span className="text-[14px] text-left text-black font-bold leading-tight">
              HEXA'S Education
            </span>
            <div className="flex items-center space-x-6 text-[14px] text-black font-medium">
              <span
                onMouseEnter={() => setShowSeconds(true)}
                onMouseLeave={() => setShowSeconds(false)}
                className="cursor-pointer"
              >
                {formatTime(timeRemaining)}
              </span>
            </div>
          </div>
          {/* Right: Icons and Finish Button */}
          <div className="flex items-center space-x-5">
            <button
              onClick={() => navigate('/')} 
              className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
            >
              Back Home
            </button>
            <button
              onClick={() => setShowHelp(true)}
              className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
            >
              Finish test
            </button>
            <div className="flex items-center space-x-8 pl-2 pr-2">
              {/* WiFi Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-9 h-9 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M17.778 8.222c-4.296-4.296-11.26-4.296-15.556 0A1 1 0 010.808 6.808c5.076-5.077 13.308-5.077 18.384 0a1 1 0 01-1.414 1.414zM14.95 11.05a7 7 0 00-9.9 0 1 1 0 01-1.414-1.414 9 9 0 0112.728 0 1 1 0 01-1.414 1.414zM12.12 13.88a3 3 0 00-4.24 0 1 1 0 01-1.415-1.415 5 5 0 717.07 0 1 1 0 01-1.415 1.415zM9 16a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              {/* Notification Bell Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                </svg>
              </div>
              {/* Hamburger Menu Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              {/* Pen/Edit Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        {/* Title Bar (fixed under top bar) */}
        <div className="relative bg-[#F4F7FA] shadow-sm border border-black rounded-lg mx-12 mt-4 mb-0 sticky top-[32px] z-20 flex items-center justify-between">
          {/* Left: Part Title */}
          <div className="flex-1 px-4 py-2 text-left">
            <h2 className="text-[23px] text-black">Part 1</h2>
            <h1 className="text-[18px] text-black">HEXA'S Reading Course Book</h1>
          </div>
          {/* Center: Absolutely Positioned Logo */}
          <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <img
              src={HEXASLogo}
              alt="HEXA'S Logo"
              className="h-[50px] object-contain"
            />
          </div>
          {/* Right: Empty space for consistency */}
          <div className="flex items-center justify-end w-[300px] pr-4">
            {/* Empty space - no audio player for reading */}
          </div>
        </div>
      </div>
      {/* Main content (scrollable) */}
      <div className="flex-1 flex flex-col items-center justify-start w-full">
        <div ref={highlightRef}
          className="flex flex-row w-full overflow-hidden -mb-16 mt-2 px-12 pb-20"
          style={{ height: 'calc(100vh - 180px)' }}
        >
          {/* Left: Reading Passage */}
          <div className="bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 w-[49%] h-full max-h-full overflow-y-auto">
            <div className="mb-8">
              <h2 className="text-[16px] font-bold mb-4">Worldly Wealth</h2>

              {/* First paragraph - centered */}
              <p className="text-[14px] italic mb-4 text-center">
                Can the future population of the world enjoy a comfortable lifestyle, with possessions, space and mobility, without crippling the environment?
              </p>

              {/* Remaining paragraphs - left aligned */}
              <div className="text-[14px] space-y-4 text-left">
                <p>
                  The world's population is expected to stabilize at around nine billion. Will it be possible for nine billion people to have the lifestyle enjoyed today only by the wealthy? One school of thought says no: not only should the majority of the world's people resign themselves to poverty forever, but rich nations must also revert to simpler lifestyles in order to save the planet.
                </p>
                <p>
                  Admittedly, there may be political or social barriers to achieving a rich world. But in fact there seems to be no insuperable physical or ecological reason why nine billion people should not achieve a comfortable lifestyle, using technology only slightly more advanced than that which we now possess. In thinking about the future of civilization, we ought to start by asking what people want. The evidence demonstrates that as people get richer, they want a greater range of personal technology, they want lots of room (preferably near or in natural surroundings) and they want greater speed in travel. More possessions, more space, more mobility.
                </p>
                <p>
                  In the developed world, the personal technologies of the wealthy, including telephones, washing machines and cars, have become necessities within a generation or two. Increasing productivity that results in decreasing costs for such goods has been responsible for the greatest gains in the standard of living, and there is every reason to believe that this will continue.
                </p>
                <p>
                  As affluence grows, the amount of energy and raw materials used for production of machinery will therefore escalate. But this need not mean an end to the machine age. Rather than being thrown away, materials from old machinery can be recycled by manufacturers. And long before all fossil fuels are exhausted, their rising prices may compel industrial society not only to become more energy efficient but also to find alternative energy sources sufficient for the demands of an advanced technological civilization: nuclear fission, nuclear fusion, solar energy, chemical photosynthesis, geothermal, biomass or some yet unknown source of energy.
                </p>
                <p>
                  The growth of cities and suburbs is often seen as a threat to the environment. However, in fact the increasing amount of land consumed by agriculture is a far greater danger than urban sprawl. Stopping the growth of farms is the best way to preserve many of the world's remaining wild areas. But is a dramatic downsizing of farmland possible? Thanks to the growth of agricultural productivity, reforestation and 're-wilding' has been under way in the industrial countries for generations. Since 1950 more land in the US has been set aside in parks than has been occupied by urban and suburban growth. And much of what was farmland in the nineteenth century is now forest again. Taking the best Iowa maize growers as the norm for world food productivity, it has been calculated that less than a tenth of present cropland could support a population of 10 billion.
                </p>
                <p>
                  In <em>The Environment Game</em>, a vision of a utopia that would be at once high-tech and environmentalist, Nigel Calder suggested that 'nourishing but unpalatable primary food produced by industrial techniques - like yeast from petroleum may be fed to animals, so that we can continue to eat our customary meat, eggs, milk, butter, and cheese and so that people in underdeveloped countries can have adequate supplies of animal protein for the first time.'
                </p>
                <p>
                  In the long run, tissue-cloning techniques could be used to grow desired portions of meat by themselves. Once their DNA has been extracted to create cow-less steaks and chicken-less drumsticks, domesticated species of livestock, bred for millennia to be stupid or to have grotesquely enhanced traits, should be allowed to become extinct, except for a few specimens in zoos. However, game such as wild deer, rabbits and wild ducks will be ever more abundant as farms revert to wilderness, so this could supplement the laboratory-grown meat in the diets of tomorrow's affluent.
                </p>
                <p>
                  With rising personal incomes come rising expectations of mobility. This is another luxury of today's rich that could become a necessity of tomorrow's global population — particularly if its members choose to live widely dispersed in a post-agrarian wilderness. In his recent book <em>Free Flight</em>, James Fallows, a pilot as well as a writer, describes serious attempts by both state and private entrepreneurs in the USA to promote an 'air taxi' system within the price range of today's middle class and perhaps tomorrow's global population.
                </p>
                <p>
                  Two of the chief obstacles to the science fiction fantasy of the personal plane or hover car are price and danger. While technological improvements are driving prices down, piloting an aircraft in three dimensions is still more difficult than driving a car in two, and pilot error causes more fatalities than driver error. But before long our aircraft and cars will be piloted by computers which are never tired or stressed.
                </p>
                <p>
                  So perhaps there are some grounds for optimism when viewing the future of civilization. With the help of technology, and without putting serious strains on the global environment, possessions, space and mobility can be achieved for all the projected population of the world.
                </p>
              </div>
            </div>
          </div>


          {/* Right: Questions */}
          <div
            ref={rightColRef}
            className="w-[50%] flex flex-col items-start bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 h-full max-h-full overflow-y-auto"
          >
            {/* Questions 14-19 */}
            <div className="mb-10 w-full">
              <h3 className="text-[15px] font-bold mb-4 text-left">Questions 14 – 19</h3>
              <p className="text-[14px] mb-6 text-left">
                Choose <strong>YES</strong> if the statement agrees with the writer's claims in the text, choose <strong>NO</strong> if the statement contradicts the writer's claims, or choose <strong>NOT GIVEN</strong> if it is impossible to say what the writer thinks about this.
              </p>

              <div className="space-y-2">
                {[
                  "Today's wealthy people ignore the fact that millions are living in poverty.",
                  "There are reasons why the future population of the world may not enjoy a comfortable lifestyle.",
                  "The first thing to consider when planning for the future is environmental protection.",
                  "As manufactured goods get cheaper, people will benefit more from them.",
                  "It may be possible to find new types of raw materials for use in the production of machinery.",
                  "The rising prices of fossil fuels may bring some benefits."
                ].map((questionText, index) => {
                  const questionNum = 14 + index;
                  const isOpen = currentQuestion === questionNum;

                  return (
                    <div
                      key={questionNum}
                      className="mb-2 rounded bg-[#f5f8fc]" // ⬅️ reduced margin-bottom from mb-4 to mb-2
                    >
                      {/* Question Header */}
                      <div
                        className="px-3 py-1.5 cursor-pointer" // ⬅️ reduced vertical padding
                        onClick={() => setCurrentQuestion(isOpen ? null : questionNum)}
                      >
                        <div className="flex items-start gap-x-3"> {/* reduced horizontal gap */}
                          <span className="font-semibold text-[14px]">{questionNum}</span>
                          <span className="text-[14px]">{questionText}</span>
                        </div>
                      </div>

                      {/* Answer Options */}
                      {isOpen && (
                        <div className="mt-1 mb-2 px-4 py-2 pl-6 flex flex-col gap-2 bg-[#D7DFEC] rounded-b-md">
                          {['YES', 'NO', 'NOT GIVEN'].map((opt) => (
                            <label
                              key={opt}
                              className="flex items-center text-[13px] cursor-pointer"
                            >
                              <input
                                type="radio"
                                name={`tfnq${questionNum}`}
                                value={opt}
                                checked={answers[questionNum - 1] === opt}
                                onChange={() => {
                                  const newAnswers = [...answers];
                                  newAnswers[questionNum - 1] = opt;
                                  setAnswers(newAnswers);
                                }}
                                className="mr-2 accent-blue-600"
                                onClick={(e) => e.stopPropagation()}
                              />
                              {opt}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>






        </div>
      </div>

      {/* Bottom Navigation (fixed footer) */}
      <div className="sticky bottom-0 left-0 w-full bg-[#D7DFEC] border-t border-gray-300 z-30 flex items-center justify-between px-4 py-3">
        {/* Left: Question Buttons */}
        <div className="flex items-center space-x-1">
          {[14, 15, 16, 17, 18, 19].map((questionNumber) => (
            <button
              key={questionNumber}
              onClick={() => setCurrentQuestion(questionNumber)}
              className={`w-7 h-7 mx-0.5 rounded text-[12px] font-bold flex items-center justify-center ${currentQuestion === questionNumber
                ? 'bg-blue-500 text-white'
                : 'bg-black text-white'
                }`}
            >
              {questionNumber}
            </button>
          ))}
        </div>

        {/* Right: Arrows */}
        <div className="flex items-center space-x-2">
          {/* Backward Arrow */}
          <button
            onClick={() => {
              setCurrentQuestion((prev) => Math.max(14, prev - 1));
            }}
            className={`w-7 h-7 flex items-center justify-center rounded-full ${currentQuestion === 14 ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500'
              } text-white`}
            title="Previous Question"
            disabled={currentQuestion === 14}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Forward Arrow */}
          <button
            onClick={() => {
              setCurrentQuestion((prev) => Math.min(19, prev + 1));
            }}
            className={`w-7 h-7 flex items-center justify-center rounded-full ${currentQuestion === 19 ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500'
              } text-white`}
            title="Next Question"
            disabled={currentQuestion === 19}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>






    </div>
  );
};

export default Class14GuidedPractice01;
